import { useCallback, useEffect, useState } from 'react';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { RiskWarningDialog } from './RiskWarningDialog';
import { MarketClosedDialog } from './MarketClosedDialog';
import { useTrading } from '@/hooks/useTrading';
import { tradingService } from '@/services/trading';
import { formatCurrency } from '@/lib/utils';
import { toast } from '@/hooks/useToast';
import type { RiskWarning } from '@/types/trading';

type SizingMode = 'dollars' | 'shares';

const INITIAL_FORM_DATA = {
  symbol: '',
  quantity: '',
  dollarAmount: '',
  side: 'buy' as 'buy' | 'sell',
  orderType: 'market' as 'market' | 'limit',
  limitPrice: '',
};

export function OrderForm() {
  const {
    placeOrderAsync,
    isPlacingOrder,
    placeOrderError,
    marketStatus,
    validateOrderAsync,
    validationResult,
    isValidating,
    overrideRiskAsync,
    isOverridingRisk,
  } = useTrading();

  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [sizingMode, setSizingMode] = useState<SizingMode>('dollars');
  const [showRiskDialog, setShowRiskDialog] = useState(false);
  const [riskViolations, setRiskViolations] = useState<RiskWarning[]>([]);
  const [pendingOrderId, setPendingOrderId] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showMarketClosedDialog, setShowMarketClosedDialog] = useState(false);

  useEffect(() => {
    if (!successMessage) return;
    const timer = setTimeout(() => setSuccessMessage(null), 5000);
    return () => clearTimeout(timer);
  }, [successMessage]);

  const estimatedValue =
    sizingMode === 'shares' && formData.quantity && formData.limitPrice
      ? parseFloat(formData.quantity) * parseFloat(formData.limitPrice)
      : 0;

  const estimatedShares =
    sizingMode === 'dollars' && formData.dollarAmount && formData.limitPrice
      ? parseFloat(formData.dollarAmount) / parseFloat(formData.limitPrice)
      : 0;

  const resetForm = useCallback(() => {
    setFormData(INITIAL_FORM_DATA);
  }, []);

  const buildOrderParams = () => {
    const base = {
      symbol: formData.symbol.toUpperCase(),
      side: formData.side,
      orderType: formData.orderType,
      limitPrice: formData.orderType === 'limit' ? parseFloat(formData.limitPrice) : undefined,
    };

    if (sizingMode === 'dollars') {
      return { ...base, notional: parseFloat(formData.dollarAmount) };
    }
    return { ...base, quantity: parseFloat(formData.quantity) };
  };

  const sizeInputFilled =
    sizingMode === 'dollars' ? !!formData.dollarAmount : !!formData.quantity;

  const sizeLabel =
    sizingMode === 'dollars'
      ? formData.dollarAmount
        ? `$${formData.dollarAmount}`
        : ''
      : formData.quantity || '';

  const handleValidate = async () => {
    if (formData.symbol && sizeInputFilled) {
      try {
        const qty =
          sizingMode === 'shares'
            ? parseFloat(formData.quantity)
            : estimatedShares || 1;
        const result = await validateOrderAsync({
          symbol: formData.symbol.toUpperCase(),
          quantity: qty,
          price: formData.limitPrice ? parseFloat(formData.limitPrice) : 100,
          side: formData.side,
        });
        if (result.passed) {
          toast({
            title: 'Risk Check Passed',
            description: 'All limits within acceptable range',
            variant: 'success',
          });
        }
      } catch {
        toast({ title: 'Risk Check Failed', variant: 'destructive' });
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccessMessage(null);

    try {
      const result = await placeOrderAsync(buildOrderParams());

      if (result.marketClosed) {
        setShowMarketClosedDialog(true);
        return;
      }

      if (result.requiresConfirmation && result.riskWarnings?.length) {
        setPendingOrderId(result.order.id);
        setRiskViolations(result.riskWarnings);
        setShowRiskDialog(true);
      } else {
        const msg = `Order placed: ${formData.side.toUpperCase()} ${sizeLabel} ${formData.symbol.toUpperCase()}`;
        setSuccessMessage(msg);
        toast({ title: 'Order Placed', description: msg, variant: 'success' });
        resetForm();
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to place order';
      toast({ title: 'Order Failed', description: msg, variant: 'destructive' });
    }
  };

  const handleRiskOverride = async () => {
    if (!pendingOrderId) return;

    try {
      await overrideRiskAsync(pendingOrderId);
      setShowRiskDialog(false);
      setRiskViolations([]);
      setPendingOrderId(null);
      const msg = `Order confirmed with risk override: ${formData.side.toUpperCase()} ${sizeLabel} ${formData.symbol.toUpperCase()}`;
      setSuccessMessage(msg);
      toast({ title: 'Order Confirmed', description: msg, variant: 'success' });
      resetForm();
    } catch {
      // Override failed -- dialog stays open
    }
  };

  const handleRiskCancel = () => {
    if (pendingOrderId) {
      tradingService.cancelOrder(pendingOrderId).catch(() => {});
    }
    setShowRiskDialog(false);
    setRiskViolations([]);
    setPendingOrderId(null);
  };

  const errorMessage =
    placeOrderError instanceof Error
      ? placeOrderError.message
      : placeOrderError
        ? String(placeOrderError)
        : null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm font-medium">Place Order</CardTitle>
        {marketStatus && (
          <div className={`text-xs ${marketStatus.isOpen ? 'text-green-500' : 'text-yellow-500'}`}>
            Market{' '}
            {marketStatus.isOpen
              ? 'Open'
              : marketStatus.isExtendedHours
                ? 'Extended Hours'
                : 'Closed'}
          </div>
        )}
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {errorMessage && (
            <div className="flex items-start gap-2 border border-red-200 bg-red-50 dark:bg-red-900/20 rounded p-3 text-sm">
              <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400 mt-0.5 shrink-0" />
              <p className="text-red-600 dark:text-red-400">{errorMessage}</p>
            </div>
          )}

          {successMessage && (
            <div className="flex items-start gap-2 border border-green-200 bg-green-50 dark:bg-green-900/20 rounded p-3 text-sm">
              <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400 mt-0.5 shrink-0" />
              <p className="text-green-600 dark:text-green-400">{successMessage}</p>
            </div>
          )}

          {/* Symbol input */}
          <div>
            <label className="text-sm font-medium">Symbol</label>
            <Input
              value={formData.symbol}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, symbol: e.target.value.toUpperCase() }))
              }
              placeholder="AAPL"
              required
            />
          </div>

          {/* Sizing mode toggle */}
          <Tabs
            value={sizingMode}
            onValueChange={(v) => setSizingMode(v as SizingMode)}
          >
            <TabsList className="w-full">
              <TabsTrigger value="dollars" className="flex-1">Dollars</TabsTrigger>
              <TabsTrigger value="shares" className="flex-1">Shares</TabsTrigger>
            </TabsList>
          </Tabs>

          {/* Size input based on mode */}
          {sizingMode === 'dollars' ? (
            <div>
              <label htmlFor="dollar-amount" className="text-sm font-medium">Amount</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                <Input
                  id="dollar-amount"
                  type="number"
                  min="1"
                  step="0.01"
                  value={formData.dollarAmount}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, dollarAmount: e.target.value }))
                  }
                  placeholder="5000.00"
                  className="pl-7"
                  required
                />
              </div>
            </div>
          ) : (
            <div>
              <label htmlFor="share-quantity" className="text-sm font-medium">Quantity</label>
              <Input
                id="share-quantity"
                type="number"
                min="1"
                value={formData.quantity}
                onChange={(e) => setFormData((prev) => ({ ...prev, quantity: e.target.value }))}
                placeholder="10"
                required
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Side</label>
              <div className="flex gap-2 mt-1">
                <Button
                  type="button"
                  variant={formData.side === 'buy' ? 'default' : 'outline'}
                  size="sm"
                  className={formData.side === 'buy' ? 'bg-green-600 hover:bg-green-700' : ''}
                  onClick={() => setFormData((prev) => ({ ...prev, side: 'buy' }))}
                >
                  Buy
                </Button>
                <Button
                  type="button"
                  variant={formData.side === 'sell' ? 'default' : 'outline'}
                  size="sm"
                  className={formData.side === 'sell' ? 'bg-red-600 hover:bg-red-700' : ''}
                  onClick={() => setFormData((prev) => ({ ...prev, side: 'sell' }))}
                >
                  Sell
                </Button>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Type</label>
              <div className="flex gap-2 mt-1">
                <Button
                  type="button"
                  variant={formData.orderType === 'market' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFormData((prev) => ({ ...prev, orderType: 'market' }))}
                >
                  Market
                </Button>
                <Button
                  type="button"
                  variant={formData.orderType === 'limit' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFormData((prev) => ({ ...prev, orderType: 'limit' }))}
                >
                  Limit
                </Button>
              </div>
            </div>
          </div>

          {formData.orderType === 'limit' && (
            <div>
              <label className="text-sm font-medium">Limit Price</label>
              <Input
                type="number"
                step="0.01"
                value={formData.limitPrice}
                onChange={(e) => setFormData((prev) => ({ ...prev, limitPrice: e.target.value }))}
                placeholder="150.00"
                required
              />
            </div>
          )}

          {/* Estimated value for shares mode */}
          {sizingMode === 'shares' && estimatedValue > 0 && (
            <div className="text-sm text-muted-foreground">
              Estimated Value: {formatCurrency(estimatedValue)}
            </div>
          )}

          {/* Estimated shares for dollars + limit mode */}
          {sizingMode === 'dollars' && estimatedShares > 0 && (
            <div className="text-sm text-muted-foreground">
              ~{estimatedShares.toFixed(2)} shares
            </div>
          )}

          {validationResult && !validationResult.passed && (
            <div className="border border-red-200 bg-red-50 dark:bg-red-900/20 rounded p-3 text-sm">
              <p className="font-medium text-red-600 dark:text-red-400 mb-2">Risk Warnings:</p>
              <ul className="space-y-1">
                {validationResult.violations.map((v, i) => (
                  <li key={`${v.rule}-${i}`} className="text-red-600 dark:text-red-400">
                    &bull; {v.message}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {validationResult && validationResult.passed && (
            <div className="flex items-start gap-2 border border-green-200 bg-green-50 dark:bg-green-900/20 rounded p-3 text-sm">
              <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400 mt-0.5 shrink-0" />
              <p className="text-green-600 dark:text-green-400">
                Risk check passed — all limits within acceptable range
              </p>
            </div>
          )}

          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleValidate}
              disabled={isValidating || !formData.symbol || !sizeInputFilled}
            >
              {isValidating ? 'Checking...' : 'Check Risk'}
            </Button>
            <Button
              type="submit"
              disabled={isPlacingOrder}
              className={
                formData.side === 'buy'
                  ? 'bg-green-600 hover:bg-green-700'
                  : 'bg-red-600 hover:bg-red-700'
              }
            >
              {isPlacingOrder
                ? 'Placing...'
                : `${formData.side === 'buy' ? 'Buy' : 'Sell'} ${formData.symbol || 'Stock'}`}
            </Button>
          </div>
        </form>

        {showRiskDialog && riskViolations.length > 0 && (
          <RiskWarningDialog
            warnings={riskViolations}
            onConfirm={handleRiskOverride}
            onCancel={handleRiskCancel}
            isLoading={isOverridingRisk}
          />
        )}

        <MarketClosedDialog
          open={showMarketClosedDialog}
          symbol={formData.symbol.toUpperCase()}
          side={formData.side}
          quantity={sizingMode === 'shares' ? formData.quantity : formData.dollarAmount}
          onQueue={async () => {
            setShowMarketClosedDialog(false);
            try {
              const params = buildOrderParams();
              const tif = 'notional' in params ? 'gtc' : 'opg';
              await placeOrderAsync({ ...params, timeInForce: tif });
              const msg = `Order queued for market open: ${formData.side.toUpperCase()} ${sizeLabel} ${formData.symbol.toUpperCase()}`;
              setSuccessMessage(msg);
              toast({ title: 'Order Queued', description: msg, variant: 'success' });
              resetForm();
            } catch (err) {
              const msg =
                err instanceof Error ? err.message : 'Failed to queue order for market open';
              toast({ title: 'Queue Failed', description: msg, variant: 'destructive' });
            }
          }}
          onCancel={() => setShowMarketClosedDialog(false)}
        />
      </CardContent>
    </Card>
  );
}

export default OrderForm;
